import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb.component';
import { ApplicationSimplifyService } from '../application-simplify.service';
import { Router } from '@angular/router';
import { WebinarSimplifyDto } from 'src/app/model/webinar-simplify.dto';
import { finalize, forkJoin, map, tap, timer } from 'rxjs';

@Component({
  selector: 'app-create-webinaire',
  templateUrl: './create-webinaire.component.html',
  styleUrls: ['./create-webinaire.component.css'],
})
export class CreateWebinaireComponent implements OnInit {
  webinaireForm!: FormGroup;
  valid = false;
  isSaving = false;
  inValid = false;
  error = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;
  isClosing = false;
  inValidName = false;
  inValidShortname = false;
  inValidDisplayName = false;
  inValidDate = false;
  inValidStartTime = false;
  inValidEndTime = false;
  inValidDescription = false;
  inValidObjectif = false;
  inValidPlatform = false;
  inValidPeriod = false;
  inValidZoomAccount = false;
  inValidUrlJokko = false;
  inValidCohorte = false;
  cohorteOptions: string[] = [];
  webinaireTypeOptions: { value: string; label: string }[] = [];
  loadingWebinaireTypes = false;
  webinaireComposanteOptions: { value: string; label: string }[] = [
    { value: 'FCC', label: 'FCC' },
    { value: 'AIPEN', label: 'AIPEN' },
    { value: 'FODE', label: 'FODE' },
    { value: 'FINANCE', label: 'FINANCE' },
    { value: 'STEMD', label: 'STEMD' },
    { value: 'COMMUNICATION', label: 'COMMUNICATION' },
    { value: 'BENIN', label: 'BENIN' },
  ];
  dureePeriode: number | null = null;
  dureePeriodeLabel = '';

  showConfirmWebinaireModal = false;
  pendingWebinaireDto: WebinarSimplifyDto | null = null;

  user: any;
  iuf!: string;

  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'SIGUI' },
    {
      label: 'GESTION DES CERTIFICATS ET WEBINAIRES',
      url: '/dashboard/appels/sigui-certificat',
    },
    { label: 'CRÉER UN WEBINAIRE', url: '/dashboard/appels/webinaire/create' },
  ];

  constructor(
    private fb: FormBuilder,
    private applicationSimplifyService: ApplicationSimplifyService,
    private router: Router,
  ) {
    this.user = sessionStorage.getItem('user');
    this.iuf = JSON.parse(this.user).iuf;
  }

  private readonly defaultFormValues = {
    name: '',
    shortname: '',
    displayName: '',
    date: '',
    startTime: '',
    endTime: '',
    description: '',
    objectif: '',
    platform: '',
    urlJokko: '',
    zoomAccount: '',
    webinaireType: '',
    webinaireComposante: '',
    periodStart: '',
    periodEnd: '',
    periodMode: 'periode',
    cohorte: '',
  };
  private readonly RENFORCEMENT_KEYWORD = 'renforcement';
  get isRenforcement(): boolean {
    const type = this.webinaireForm?.get('webinaireType')?.value;
    return this.normalize(type).includes(this.RENFORCEMENT_KEYWORD);
  }

  get periodMode(): 'periode' | 'cohorte' {
    return this.webinaireForm?.get('periodMode')?.value;
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .trim();
  }

  selectPeriodMode(mode: 'periode' | 'cohorte', event?: Event): void {
    const input = event?.target as HTMLInputElement | null;
    if (input) input.checked = true;
    this.webinaireForm.get('periodMode')?.setValue(mode);
  }

  ngOnInit(): void {
    this.loadWebinaireTypeOptions();
    this.webinaireForm = this.fb.group({
      name: ['', Validators.required],
      shortname: ['', Validators.required],
      displayName: ['', Validators.required],
      date: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      description: [''],
      objectif: [''],
      platform: ['', Validators.required],
      urlJokko: [''],
      zoomAccount: [''],
      webinaireType: ['', Validators.required],
      webinaireComposante: [''],
      periodStart: [''],
      periodEnd: [''],
      periodMode: ['periode'],
      cohorte: [''],
    });
    this.webinaireForm
      .get('platform')
      ?.valueChanges.subscribe((platformValue) => {
        this.inValidZoomAccount = false;
        this.inValidUrlJokko = false;
        this.applyPlatformValidators(platformValue);
      });
    this.applyPlatformValidators(this.webinaireForm.get('platform')?.value);
    this.applyPeriodModeValidators(
      this.isRenforcement,
      this.webinaireForm.get('periodMode')?.value,
    );

    this.webinaireForm
      .get('periodStart')
      ?.valueChanges.subscribe(() => this.calculateDureePeriode());
    this.webinaireForm
      .get('periodEnd')
      ?.valueChanges.subscribe(() => this.calculateDureePeriode());
    this.webinaireForm
      .get('webinaireType')
      ?.valueChanges.subscribe(() => {
        if (!this.isRenforcement) {
          this.webinaireForm
            .get('periodStart')
            ?.setValue('', { emitEvent: false });
          this.webinaireForm
            .get('periodEnd')
            ?.setValue('', { emitEvent: false });
          this.webinaireForm
            .get('cohorte')
            ?.setValue('', { emitEvent: false });
          this.webinaireForm
            .get('periodMode')
            ?.setValue('periode', { emitEvent: false });
          this.dureePeriode = null;
          this.dureePeriodeLabel = '';
          this.inValidPeriod = false;
          this.inValidCohorte = false;
          this.applyPeriodModeValidators(false);
        } else {
          if (!this.cohorteOptions.length) {
            this.loadRecentCohortes();
          }
          this.applyPeriodModeValidators(
            true,
            this.webinaireForm.get('periodMode')?.value,
          );
        }
      });

    this.webinaireForm
      .get('periodMode')
      ?.valueChanges.subscribe((mode) => {
        this.applyPeriodModeValidators(this.isRenforcement, mode);
      });

    this.webinaireForm.get('cohorte')?.valueChanges.subscribe((value) => {
      if (value) this.inValidCohorte = false;
    });
    const fields: { control: string; flag: keyof CreateWebinaireComponent }[] =
      [
        { control: 'name', flag: 'inValidName' },
        { control: 'shortname', flag: 'inValidShortname' },
        { control: 'displayName', flag: 'inValidDisplayName' },
        { control: 'date', flag: 'inValidDate' },
        { control: 'startTime', flag: 'inValidStartTime' },
        { control: 'endTime', flag: 'inValidEndTime' },
        { control: 'description', flag: 'inValidDescription' },
        { control: 'objectif', flag: 'inValidObjectif' },
      ];

    fields.forEach(({ control, flag }) => {
      this.webinaireForm.get(control)?.valueChanges.subscribe((value) => {
        if (value?.length > 0) {
          (this as any)[flag] = false;
        }
      });
    });

    this.webinaireForm.get('zoomAccount')?.valueChanges.subscribe((value) => {
      if (value) this.inValidZoomAccount = false;
    });
    this.webinaireForm.get('urlJokko')?.valueChanges.subscribe((value) => {
      if (value) this.inValidUrlJokko = false;
    });
  }
  private applyPlatformValidators(platform: string | null): void {
    const urlControl = this.webinaireForm.get('urlJokko');
    const zoomAccountControl = this.webinaireForm.get('zoomAccount');

    if (platform === 'jokko') {
      urlControl?.setValidators([Validators.required]);
      zoomAccountControl?.clearValidators();
      zoomAccountControl?.setValue('', { emitEvent: false });
    } else if (platform === 'zoom') {
      zoomAccountControl?.setValidators([Validators.required]);
      urlControl?.clearValidators();
      urlControl?.setValue('', { emitEvent: false });
    } else {
      urlControl?.clearValidators();
      zoomAccountControl?.clearValidators();
    }
    urlControl?.updateValueAndValidity();
    zoomAccountControl?.updateValueAndValidity();
  }

  private applyPeriodModeValidators(
    active: boolean,
    mode: string | null = 'periode',
  ): void {
    const cohorteControl = this.webinaireForm.get('cohorte');
    const periodStartControl = this.webinaireForm.get('periodStart');
    const periodEndControl = this.webinaireForm.get('periodEnd');

    if (!active) {
      cohorteControl?.clearValidators();
      periodStartControl?.clearValidators();
      periodEndControl?.clearValidators();
    } else if (mode === 'cohorte') {
      cohorteControl?.setValidators([Validators.required]);
      periodStartControl?.clearValidators();
      periodEndControl?.clearValidators();
      periodStartControl?.setValue('', { emitEvent: false });
      periodEndControl?.setValue('', { emitEvent: false });
      this.dureePeriode = null;
      this.dureePeriodeLabel = '';
      this.inValidPeriod = false;
    } else {
      periodStartControl?.setValidators([Validators.required]);
      periodEndControl?.setValidators([Validators.required]);
      cohorteControl?.clearValidators();
      cohorteControl?.setValue('', { emitEvent: false });
      this.inValidCohorte = false;
    }

    cohorteControl?.updateValueAndValidity({ emitEvent: false });
    periodStartControl?.updateValueAndValidity({ emitEvent: false });
    periodEndControl?.updateValueAndValidity({ emitEvent: false });
  }

  private loadRecentCohortes(): void {
    this.applicationSimplifyService.getRecentCohorte().subscribe({
      next: (list) => (this.cohorteOptions = list || []),
      error: () => (this.cohorteOptions = []),
    });
  }

  private loadWebinaireTypeOptions(): void {
    this.loadingWebinaireTypes = true;
    this.applicationSimplifyService.getWebinarTypeOptions().subscribe({
      next: (types) => {
        this.webinaireTypeOptions = (types || []).map((type) => ({
          value: type,
          label: type,
        }));
        this.loadingWebinaireTypes = false;
      },
      error: () => {
        this.webinaireTypeOptions = [];
        this.loadingWebinaireTypes = false;
      },
    });
  }

  openTimePicker(input: HTMLInputElement): void {
    const el = input as HTMLInputElement & { showPicker?: () => void };
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
      }
    } catch { }
    input.focus();
  }

  private calculateDureePeriode(): void {
    const start = this.webinaireForm.get('periodStart')?.value;
    const end = this.webinaireForm.get('periodEnd')?.value;

    if (!start || !end) {
      this.dureePeriode = null;
      this.dureePeriodeLabel = '';
      this.inValidPeriod = false;
      return;
    }

    const startDate = new Date(start);
    const endDate = new Date(end);
    const oneDayMs = 1000 * 60 * 60 * 24;
    const diffDays =
      Math.round((endDate.getTime() - startDate.getTime()) / oneDayMs) + 1;

    if (diffDays < 1) {
      this.dureePeriode = null;
      this.dureePeriodeLabel = '';
      this.inValidPeriod = true;
      return;
    }

    this.inValidPeriod = false;
    this.dureePeriode = diffDays;
    this.dureePeriodeLabel = diffDays === 1 ? '1 jour' : `${diffDays} jours`;
  }

  createWebinaire(): void {
    if (this.isSaving) return;
    if (this.webinaireForm.valid) {
      const formValue = this.webinaireForm.value;

      const formatTime = (time: string): string => {
        return time?.length === 5 ? `${time}:00` : time;
      };

      this.pendingWebinaireDto = {
        name: formValue.name,
        shortname: formValue.shortname,
        displayName: formValue.displayName,
        date: formValue.date,
        startTime: formatTime(formValue.startTime),
        endTime: formatTime(formValue.endTime),
        description: formValue.description,
        objectif: formValue.objectif,
        plateforme: formValue.platform,
        urlJokko: formValue.urlJokko,
        zoomAccount:
          formValue.platform === 'zoom' ? formValue.zoomAccount : null,
        webinaireType: formValue.webinaireType || null,
        webinaireComposante: formValue.webinaireComposante || null,
        cohorte:
          this.isRenforcement &&
            formValue.periodMode === 'cohorte'
            ? formValue.cohorte
            : null,
        periodStart:
          this.isRenforcement &&
            formValue.periodMode === 'periode'
            ? formValue.periodStart || null
            : null,
        periodEnd:
          this.isRenforcement &&
            formValue.periodMode === 'periode'
            ? formValue.periodEnd || null
            : null,
        //dureePeriode: this.dureePeriode,
      };
      // console.log('DTO à envoyer:', this.pendingWebinaireDto);

      this.showConfirmWebinaireModal = true;
    } else {
      window.scrollTo(0, 0);
      this.inValid = true;
      setTimeout(() => (this.inValid = false), 3000);
      this.markInvalidFields();
    }
  }

  private readonly MIN_SPINNER_MS = 600;

  confirmWebinaireCreation(): void {
    const dto = this.pendingWebinaireDto!;
    this.showConfirmWebinaireModal = false;
    this.isSaving = true;

    forkJoin([
      this.applicationSimplifyService.createWebinaire(dto),
      timer(this.MIN_SPINNER_MS),
    ])
      .pipe(
        map(([response]) => response),
        tap((response: any) => {
          if (response.status === 201 || response) {
            window.scrollTo(0, 0);
            this.webinaireForm.reset(this.defaultFormValues);
            this.clearInvalidFlags();
            this.dureePeriode = null;
            this.dureePeriodeLabel = '';

            this.successMessage = 'Le webinaire a été créé avec succès.';
            setTimeout(() => {
              this.isClosing = true;
            }, 2300);

            setTimeout(() => {
              this.router.navigate(['/dashboard/appels/sigui-certificat']);
            }, 2800);
          } else {
            window.scrollTo(0, 0);

            this.errorMessage =
              'Une erreur est survenue lors de la création du webinaire. Veuillez réessayer.';

            setTimeout(() => {
              this.errorMessage = null;
            }, 3000);
          }
        }),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        error: (err: any) => {
          window.scrollTo(0, 0);

          if (err.status === 409 && err.error?.message) {
            this.errorMessage = err.error.message;
          } else {
            this.errorMessage =
              'Une erreur est survenue lors de la création du webinaire. Veuillez réessayer.';
          }

          setTimeout(() => {
            this.errorMessage = null;
          }, 3000);
        },
      });

    this.pendingWebinaireDto = null;
  }

  cancelWebinaireCreation(): void {
    this.showConfirmWebinaireModal = false;
    this.pendingWebinaireDto = null;
  }

  private markInvalidFields(): void {
    const checks: { field: string; flag: keyof CreateWebinaireComponent }[] = [
      { field: 'name', flag: 'inValidName' },
      { field: 'shortname', flag: 'inValidShortname' },
      { field: 'displayName', flag: 'inValidDisplayName' },
      { field: 'date', flag: 'inValidDate' },
      { field: 'startTime', flag: 'inValidStartTime' },
      { field: 'endTime', flag: 'inValidEndTime' },
      { field: 'platform', flag: 'inValidPlatform' },
      { field: 'zoomAccount', flag: 'inValidZoomAccount' },
      { field: 'urlJokko', flag: 'inValidUrlJokko' },
    ];

    checks.forEach(({ field, flag }) => {
      (this as any)[flag] = !!this.webinaireForm.get(field)?.invalid;
    });

    if (this.isRenforcement) {
      if (this.webinaireForm.get('periodMode')?.value === 'cohorte') {
        this.inValidCohorte = !!this.webinaireForm.get('cohorte')?.invalid;
        this.inValidPeriod = false;
      } else {
        this.inValidPeriod = !!(
          this.webinaireForm.get('periodStart')?.invalid ||
          this.webinaireForm.get('periodEnd')?.invalid
        );
        this.inValidCohorte = false;
      }
    } else {
      this.inValidCohorte = false;
      this.inValidPeriod = false;
    }

    this.webinaireForm.markAllAsTouched();
  }

  private clearInvalidFlags(): void {
    this.inValid = false;
    this.inValidName = false;
    this.inValidShortname = false;
    this.inValidDisplayName = false;
    this.inValidDate = false;
    this.inValidStartTime = false;
    this.inValidEndTime = false;
    this.inValidDescription = false;
    this.inValidObjectif = false;
    this.inValidPlatform = false;
    this.inValidPeriod = false;
    this.inValidZoomAccount = false;
    this.inValidUrlJokko = false;
    this.inValidCohorte = false;
  }

  resetCreation(): void {
    if (this.isSaving) return;
    this.webinaireForm.reset(this.defaultFormValues);
    this.clearInvalidFlags();
    this.dureePeriode = null;
    this.dureePeriodeLabel = '';
    window.scrollTo(0, 0);
  }
}